import { useState, useEffect, useRef } from 'react';
import { getClients, createClient, updateClient, deleteClient, getUsers, getCustomFields, assignAccountToClient, checkClientName } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { MultiSelect } from '../components/ui/multi-select';
import { Plus, MoreHorizontal, Pencil, Trash2, Building2, Search, Loader2, UserCog } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Checkbox } from '../components/ui/checkbox';
import ClientAccountHistory from '../components/ClientAccountHistory';

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [accountUsers, setAccountUsers] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSettori, setFilterSettori] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  // Bulk selection state
  const [selectedClients, setSelectedClients] = useState([]);
  const [bulkAssignDialogOpen, setBulkAssignDialogOpen] = useState(false);
  const [bulkAssignAccountId, setBulkAssignAccountId] = useState('');
  const [bulkAssigning, setBulkAssigning] = useState(false);

  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    address: '',
    regione: '',
    notes: '',
    assigned_account_id: '',
    settore: '',
    contact_persons: [],
    custom_fields: {},
  });
  const [nameCheck, setNameCheck] = useState({ exact: [], similar: [], checking: false });
  const nameCheckTimerRef = useRef(null);

  // Debounce del check-name su company_name
  useEffect(() => {
    if (nameCheckTimerRef.current) clearTimeout(nameCheckTimerRef.current);
    const name = (formData.company_name || '').trim();
    if (name.length < 2) {
      setNameCheck({ exact: [], similar: [], checking: false });
      return;
    }
    setNameCheck((s) => ({ ...s, checking: true }));
    nameCheckTimerRef.current = setTimeout(async () => {
      try {
        const res = await checkClientName(name, editingClient?.id || null);
        setNameCheck({ exact: res.data.exact || [], similar: res.data.similar || [], checking: false });
      } catch {
        setNameCheck({ exact: [], similar: [], checking: false });
      }
    }, 400);
    return () => { if (nameCheckTimerRef.current) clearTimeout(nameCheckTimerRef.current); };
  }, [formData.company_name, editingClient]);

  const SETTORI = [
    'Agricoltura e Agroalimentare', 'Industria e Produzione', 'Edilizia e Immobiliare',
    'Energia e Ambiente', 'Trasporti e Logistica', 'Commercio (Retail & Wholesale)',
    'Tecnologia e IT', 'Marketing e Comunicazione', 'Consulenza e Servizi B2B',
    'Finanza e Assicurazioni', 'Sanità e Benessere', 'Formazione ed Educazione',
    'Turismo e Hospitality', 'Ristorazione e Food Service', 'Media e Intrattenimento',
    'Sport e Fitness', 'Moda e Lusso', 'Automotive', 'Pubblica Amministrazione',
    'No Profit e Associazioni', 'Altro'
  ];

  const REGIONI = [
    'Abruzzo', 'Basilicata', 'Calabria', 'Campania', 'Emilia-Romagna',
    'Friuli-Venezia Giulia', 'Lazio', 'Liguria', 'Lombardia', 'Marche',
    'Molise', 'Piemonte', 'Puglia', 'Sardegna', 'Sicilia', 'Toscana',
    "Trentino-Alto Adige", 'Umbria', "Valle d'Aosta", 'Veneto',
    'Fuori Italia',
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [clientsRes, accountUsersRes, fieldsRes] = await Promise.all([
        getClients(),
        getUsers('account'),  // Carica utenti Account Manager
        getCustomFields('client'),
      ]);
      setClients(clientsRes.data);
      setAccountUsers(accountUsersRes.data);
      setCustomFields(fieldsRes.data);
    } catch (err) {
      toast({ title: 'Errore', description: 'Impossibile caricare i dati', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (client = null) => {
    if (client) {
      setEditingClient(client);
      setFormData({
        company_name: client.company_name,
        contact_name: client.contact_name || '',
        email: client.email || '',
        phone: client.phone || '',
        address: client.address || '',
        regione: client.regione || '',
        notes: client.notes || '',
        assigned_account_id: client.assigned_account_id || '',
        settore: client.settore || '',
        contact_persons: client.contact_persons || [],
        custom_fields: client.custom_fields || {},
      });
    } else {
      setEditingClient(null);
      setFormData({
        company_name: '',
        contact_name: '',
        email: '',
        phone: '',
        address: '',
        regione: '',
        notes: '',
        assigned_account_id: '',
        settore: '',
        contact_persons: [],
        custom_fields: {},
      });
    }
    setDialogOpen(true);
  };

  const handleOpenExistingClient = (clientId) => {
    const existing = clients.find(c => c.id === clientId);
    if (!existing) {
      toast({ title: 'Cliente non trovato', description: 'Il cliente potrebbe essere fuori dai filtri attivi. Rimuovi eventuali filtri e riprova.', variant: 'destructive' });
      return;
    }
    // Reset dello stato di check nome per evitare warning falsi sul cliente esistente
    setNameCheck({ exact: [], similar: [], checking: false });
    // Riapre il dialog in modalità EDIT sul cliente esistente
    handleOpenDialog(existing);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validazione: Account Manager obbligatorio in creazione
    if (!editingClient && !formData.assigned_account_id) {
      toast({ title: 'Errore', description: 'Seleziona un Account Manager', variant: 'destructive' });
      return;
    }
    
    // Validazione campi obbligatori
    if (!formData.email) {
      toast({ title: 'Errore', description: 'Email è obbligatoria', variant: 'destructive' });
      return;
    }
    if (!formData.phone) {
      toast({ title: 'Errore', description: 'Telefono è obbligatorio', variant: 'destructive' });
      return;
    }
    if (!formData.settore) {
      toast({ title: 'Errore', description: 'Settore è obbligatorio', variant: 'destructive' });
      return;
    }
    if (!formData.regione) {
      toast({ title: 'Errore', description: 'Regione è obbligatoria', variant: 'destructive' });
      return;
    }
    // Persone di contatto opzionali: rimuovi entry vuote prima del submit
    formData.contact_persons = (formData.contact_persons || []).filter(cp => cp && (cp.name || cp.role || cp.email || cp.phone));
    
    // Validate required custom fields
    const missingCF = customFields.filter(f => f.is_required && !formData.custom_fields[f.id]);
    if (missingCF.length > 0) {
      toast({ title: 'Campi obbligatori', description: `Compila: ${missingCF.map(f => f.name).join(', ')}`, variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const data = { ...formData };
      const accountIdToAssign = data.assigned_account_id;
      delete data.assigned_account_id;  // Non inviare al create/update client direttamente
      if (!data.email) delete data.email;

      let clientId;
      if (editingClient) {
        await updateClient(editingClient.id, data);
        clientId = editingClient.id;
        toast({ title: 'Cliente aggiornato' });
      } else {
        const res = await createClient(data);
        clientId = res.data.id;
        toast({ title: 'Cliente creato' });
      }
      
      // Se è stato selezionato un Account Manager, assegnalo
      if (accountIdToAssign && clientId) {
        try {
          await assignAccountToClient(clientId, accountIdToAssign);
        } catch (err) {
          console.error('Error assigning account:', err);
          // Non bloccare se l'assegnazione fallisce, mostra solo un warning
          toast({ 
            title: 'Attenzione', 
            description: 'Cliente salvato ma errore nell\'assegnazione Account Manager', 
            variant: 'destructive' 
          });
        }
      }
      
      setDialogOpen(false);
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Operazione fallita', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (client) => {
    if (!window.confirm(`Eliminare il cliente "${client.company_name}"?`)) return;
    try {
      await deleteClient(client.id);
      toast({ title: 'Cliente eliminato' });
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: 'Impossibile eliminare il cliente', variant: 'destructive' });
    }
  };

  // Helper function to get account manager name
  const getAccountManagerName = (client) => {
    if (!client.assigned_account_id) return null;
    const account = accountUsers.find(u => u.id === client.assigned_account_id);
    return account ? account.name : null;
  };

  // Bulk selection functions
  const handleSelectClient = (clientId) => {
    setSelectedClients(prev => 
      prev.includes(clientId) 
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const handleSelectAll = () => {
    if (selectedClients.length === filteredClients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(filteredClients.map(c => c.id));
    }
  };

  const handleBulkAssign = async () => {
    if (!bulkAssignAccountId) {
      toast({ title: 'Errore', description: 'Seleziona un Account Manager', variant: 'destructive' });
      return;
    }

    setBulkAssigning(true);
    let successCount = 0;
    let errorCount = 0;

    for (const clientId of selectedClients) {
      try {
        await assignAccountToClient(clientId, bulkAssignAccountId);
        successCount++;
      } catch (err) {
        console.error(`Error assigning account to client ${clientId}:`, err);
        errorCount++;
      }
    }

    setBulkAssigning(false);
    setBulkAssignDialogOpen(false);
    setBulkAssignAccountId('');
    setSelectedClients([]);

    if (errorCount === 0) {
      toast({ title: 'Account Manager assegnato', description: `${successCount} clienti aggiornati` });
    } else {
      toast({ 
        title: 'Assegnazione parziale', 
        description: `${successCount} riusciti, ${errorCount} falliti`, 
        variant: 'destructive' 
      });
    }

    loadData();
  };

  const filteredClients = clients.filter((c) => {
    const searchLower = searchTerm.toLowerCase();
    const accountName = getAccountManagerName(c)?.toLowerCase() || '';
    const matchesSearch = (
      c.company_name.toLowerCase().includes(searchLower) ||
      c.contact_name?.toLowerCase().includes(searchLower) ||
      accountName.includes(searchLower)
    );
    const matchesSettore = filterSettori.length === 0 || filterSettori.includes(c.settore || '__none__');
    return matchesSearch && matchesSettore;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clienti</h1>
          <p className="text-gray-500">Gestisci le aziende clienti</p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-client-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nuovo Cliente
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap flex-1">
              <div className="relative flex-1 max-w-sm min-w-[240px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Cerca per nome del cliente o account manager..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-clients"
                />
              </div>
              <div className="min-w-[220px]">
                <MultiSelect
                  options={SETTORI.map(s => ({ value: s, label: s }))}
                  selected={filterSettori}
                  onChange={setFilterSettori}
                  placeholder="Filtra per settore"
                  data-testid="filter-settori"
                />
              </div>
              {(searchTerm || filterSettori.length > 0) && (
                <Button variant="ghost" size="sm" onClick={() => { setSearchTerm(''); setFilterSettori([]); }} data-testid="reset-filters">
                  Azzera
                </Button>
              )}
            </div>
            {selectedClients.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">
                  {selectedClients.length} selezionati
                </span>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setBulkAssignDialogOpen(true)}
                  data-testid="bulk-assign-btn"
                >
                  <UserCog className="w-4 h-4 mr-2" />
                  Assegna Account Manager
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setSelectedClients([])}
                >
                  Deseleziona
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {filteredClients.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 mx-auto text-gray-300" />
              <p className="mt-4 text-gray-500">Nessun cliente trovato</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedClients.length === filteredClients.length && filteredClients.length > 0}
                      onCheckedChange={handleSelectAll}
                      aria-label="Seleziona tutti"
                    />
                  </TableHead>
                  <TableHead>Azienda</TableHead>
                  <TableHead>Contatto</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Telefono</TableHead>
                  <TableHead>Account Manager</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClients.map((client) => (
                  <TableRow 
                    key={client.id} 
                    data-testid={`client-row-${client.id}`}
                    className={selectedClients.includes(client.id) ? "bg-orange-50" : ""}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selectedClients.includes(client.id)}
                        onCheckedChange={() => handleSelectClient(client.id)}
                        aria-label={`Seleziona ${client.company_name}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium">{client.company_name}</TableCell>
                    <TableCell>{client.contact_name || '-'}</TableCell>
                    <TableCell>{client.email || '-'}</TableCell>
                    <TableCell>{client.phone || '-'}</TableCell>
                    <TableCell>
                      {getAccountManagerName(client) || (
                        <span className="text-gray-400 text-sm">Non assegnato</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleOpenDialog(client)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Modifica
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(client)} className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Elimina
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingClient ? 'Modifica Cliente' : 'Nuovo Cliente'}</DialogTitle>
            <DialogDescription>
              {editingClient ? 'Modifica i dati del cliente' : 'Inserisci i dati del nuovo cliente'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="company_name">Nome Azienda *</Label>
              <Input
                id="company_name"
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                required
                data-testid="client-company-name"
              />
              {nameCheck.exact.length > 0 && (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2.5 py-1.5 space-y-1" data-testid="client-name-duplicate-alert">
                  <div>⚠️ Esiste già un cliente con questo nome. Non potrai salvare finché non lo modifichi.</div>
                  <div className="flex flex-wrap gap-1.5">
                    {nameCheck.exact.map(x => (
                      <button
                        key={x.id}
                        type="button"
                        onClick={() => handleOpenExistingClient(x.id)}
                        className="underline font-medium hover:text-red-900 text-left"
                        data-testid={`open-existing-client-${x.id}`}
                        title="Apri il cliente esistente"
                      >
                        → {x.company_name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {nameCheck.exact.length === 0 && nameCheck.similar.length > 0 && (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5 space-y-1" data-testid="client-name-similar-alert">
                  <div>ℹ️ Esiste già un cliente con un nome simile. Verifica che non sia un duplicato prima di salvare.</div>
                  <div className="flex flex-wrap gap-1.5">
                    {nameCheck.similar.map(x => (
                      <button
                        key={x.id}
                        type="button"
                        onClick={() => handleOpenExistingClient(x.id)}
                        className="underline font-medium hover:text-amber-900 text-left"
                        data-testid={`open-existing-client-${x.id}`}
                        title="Apri il cliente esistente"
                      >
                        → {x.company_name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact_name">Persona di Contatto</Label>
                <Input
                  id="contact_name"
                  value={formData.contact_name}
                  onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Telefono <span className="text-red-500">*</span></Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email <span className="text-red-500">*</span></Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="settore">Settore <span className="text-red-500">*</span></Label>
                <Select
                  value={formData.settore || '__none__'}
                  onValueChange={(v) => setFormData({ ...formData, settore: v === '__none__' ? '' : v })}
                >
                  <SelectTrigger className={!formData.settore ? "border-red-300" : ""}>
                    <SelectValue placeholder="Seleziona settore" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__"><span className="text-gray-400">Seleziona...</span></SelectItem>
                    {SETTORI.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="regione">Regione <span className="text-red-500">*</span></Label>
                <Select
                  value={formData.regione || '__none__'}
                  onValueChange={(v) => setFormData({ ...formData, regione: v === '__none__' ? '' : v })}
                >
                  <SelectTrigger className={!formData.regione ? "border-red-300" : ""} data-testid="client-regione-trigger">
                    <SelectValue placeholder="Seleziona regione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__"><span className="text-gray-400">Seleziona...</span></SelectItem>
                    {REGIONI.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Indirizzo</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>
            </div>

            {/* Persone di Contatto */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Persone di Contatto</Label>
                <Button type="button" variant="ghost" size="sm" className="h-7 text-xs gap-1"
                  onClick={() => setFormData({ ...formData, contact_persons: [...formData.contact_persons, { name: '', role: '', email: '', phone: '' }] })}>
                  + Aggiungi
                </Button>
              </div>
              {formData.contact_persons.map((cp, idx) => (
                <div key={idx} className="border rounded-lg p-3 space-y-2 bg-gray-50/50 relative">
                  {idx >= 0 && (
                    <button type="button" className="absolute top-2 right-2 text-gray-400 hover:text-red-500 text-xs"
                      onClick={() => setFormData({ ...formData, contact_persons: formData.contact_persons.filter((_, i) => i !== idx) })}>
                      &times;
                    </button>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Nome" className="h-8 text-xs" value={cp.name}
                      onChange={e => { const arr = [...formData.contact_persons]; arr[idx] = { ...arr[idx], name: e.target.value }; setFormData({ ...formData, contact_persons: arr }); }} />
                    <Input placeholder="Ruolo" className="h-8 text-xs" value={cp.role}
                      onChange={e => { const arr = [...formData.contact_persons]; arr[idx] = { ...arr[idx], role: e.target.value }; setFormData({ ...formData, contact_persons: arr }); }} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Email" className="h-8 text-xs" value={cp.email}
                      onChange={e => { const arr = [...formData.contact_persons]; arr[idx] = { ...arr[idx], email: e.target.value }; setFormData({ ...formData, contact_persons: arr }); }} />
                    <Input placeholder="Telefono" className="h-8 text-xs" value={cp.phone}
                      onChange={e => { const arr = [...formData.contact_persons]; arr[idx] = { ...arr[idx], phone: e.target.value }; setFormData({ ...formData, contact_persons: arr }); }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="space-y-2">
              <Label htmlFor="assigned_account">
                Account Manager {!editingClient && <span className="text-red-500">*</span>}
              </Label>
              <Select
                value={formData.assigned_account_id || "none"}
                onValueChange={(v) => setFormData({ ...formData, assigned_account_id: v === "none" ? "" : v })}
              >
                <SelectTrigger className={!editingClient && !formData.assigned_account_id ? "border-red-300" : ""}>
                  <SelectValue placeholder="Seleziona Account Manager" />
                </SelectTrigger>
                <SelectContent>
                  {editingClient && <SelectItem value="none">Nessuno</SelectItem>}
                  {accountUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name} ({u.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Account Manager History - only show when editing */}
            {editingClient && (
              <div className="border rounded-lg p-4">
                <ClientAccountHistory clientId={editingClient.id} canEdit={true} />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="notes">Note</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
              />
            </div>

            {customFields.length > 0 && (
              <div className="border rounded-lg p-4 space-y-4">
                <h4 className="font-medium">Campi Personalizzati</h4>
                {customFields.map((field) => (
                  <div key={field.id} className="space-y-2">
                    <Label>{field.name} {field.is_required && '*'}</Label>
                    {field.field_type === 'text' && (
                      <Input
                        value={formData.custom_fields[field.id] || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            custom_fields: { ...formData.custom_fields, [field.id]: e.target.value },
                          })
                        }
                        required={field.is_required}
                      />
                    )}
                    {field.field_type === 'number' && (
                      <Input
                        type="number"
                        value={formData.custom_fields[field.id] || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            custom_fields: { ...formData.custom_fields, [field.id]: e.target.value },
                          })
                        }
                        required={field.is_required}
                      />
                    )}
                    {field.field_type === 'select' && (
                      <Select
                        value={formData.custom_fields[field.id] || ''}
                        onValueChange={(v) =>
                          setFormData({
                            ...formData,
                            custom_fields: { ...formData.custom_fields, [field.id]: v === '__none__' ? '' : v },
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleziona..." />
                        </SelectTrigger>
                        <SelectContent>
                          {!field.is_required && (
                            <SelectItem value="__none__">
                              <span className="text-gray-400">Nessuno</span>
                            </SelectItem>
                          )}
                          {field.options?.map((opt) => (
                            <SelectItem key={opt} value={opt}>
                              {opt}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {field.field_type === 'boolean' && (
                      <Checkbox
                        checked={formData.custom_fields[field.id] || false}
                        onCheckedChange={(checked) =>
                          setFormData({
                            ...formData,
                            custom_fields: { ...formData.custom_fields, [field.id]: checked },
                          })
                        }
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annulla
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-client-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingClient ? 'Salva Modifiche' : 'Crea Cliente'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Account Manager Dialog */}
      <Dialog open={bulkAssignDialogOpen} onOpenChange={setBulkAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assegna Account Manager</DialogTitle>
            <DialogDescription>
              Assegna un Account Manager a {selectedClients.length} clienti selezionati.
              L'assegnazione precedente verrà chiusa automaticamente.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Account Manager</Label>
              <Select value={bulkAssignAccountId} onValueChange={setBulkAssignAccountId}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona Account Manager" />
                </SelectTrigger>
                <SelectContent>
                  {accountUsers.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm font-medium text-gray-700 mb-2">Clienti selezionati:</p>
              <div className="max-h-32 overflow-y-auto">
                {selectedClients.map(clientId => {
                  const client = clients.find(c => c.id === clientId);
                  return client ? (
                    <p key={clientId} className="text-sm text-gray-600">• {client.company_name}</p>
                  ) : null;
                })}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setBulkAssignDialogOpen(false)}>
              Annulla
            </Button>
            <Button 
              type="button" 
              onClick={handleBulkAssign} 
              disabled={bulkAssigning || !bulkAssignAccountId}
            >
              {bulkAssigning && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Assegna a {selectedClients.length} clienti
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
